import { Checkbox } from '@atlaskit/checkbox';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { ErrorMessage, Field, FormSection } from '@atlaskit/form';
import Select, { ValueType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { TransactionAccount } from '@css-xp/graphtypes';
import { BillingAdmin } from '@css-xp/graphtypes/src/generated-types-new';
import { SubscriptionTransferReasonCodes } from '@atlassian/cssxp-types-entitlement';
import React from 'react';
import { FormData } from '../subscription-txa-transfer-pane';

type Props = {
  formState: FormData;
  setCurrFormState: (formState: FormData) => void;
  setDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  transactionAccount: TransactionAccount;
};
interface SelectItem {
  label: string;
  value: string;
}

export const ConfirmSubscriptionTransfer = ({
  formState,
  transactionAccount,
  setCurrFormState,
  setDisabled,
}: Props) => {
  const [isChecked, setIsChecked] = React.useState<boolean>(false);
  const [hasBothBillingAdmins, setIfBothBillingAdmins] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (setDisabled) {
      //check if all the required form fields are present
      if (formState.customReason && formState.supportTicketLink && formState.notes && formState.termsOfTransfer) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    }
  }, [formState.customReason, formState.supportTicketLink, formState.notes, formState.termsOfTransfer, setDisabled]);

  React.useEffect(() => {
    if (formState?.currentBillingAdmin && formState?.newBillingAdmin) {
      setIfBothBillingAdmins(false);
    } else {
      setIfBothBillingAdmins(true);
    }
  }, [formState?.currentBillingAdmin, formState?.newBillingAdmin]);

  const currentBillingAdmins = transactionAccount?.billingAdmins?.nodes?.map(
    (admin: BillingAdmin | undefined | null) => ({
      label: admin?.email,
      value: admin?.email,
    })
  );

  const newBillingAdmins = formState?.selectedBillingAccount?.billingAdmins?.nodes?.map(
    (admin: BillingAdmin | undefined | null) => ({
      label: admin?.email,
      value: admin?.email,
    })
  );

  const reasonCodes = Object.values(SubscriptionTransferReasonCodes);
  const reasonCodesList = reasonCodes.map((reason: string) => ({
    label: reason,
    value: reason,
  }));

  return (
    <>
      <div className="plan-view mt-40" data-testid="confirm-form">
        <h2>Confirm subscription transfer</h2>
        <FormSection>
          <div className="form-component-width">
            <Field
              name="customReason"
              id="customReason"
              label="Reason code"
              isRequired={true}
              defaultValue={formState.customReason}
              data-testId="transfer-reason-code"
            >
              {({ fieldProps }) => (
                <Select
                  {...fieldProps}
                  isSearchable={false}
                  options={reasonCodesList}
                  data-testId="transfer-reason-code"
                  placeholder={'Select'}
                  value={reasonCodesList?.find((reason) => reason?.value === formState?.customValue)}
                  onChange={(reason: ValueType<SelectItem, false>) => {
                    formState.customReason = Object.keys(SubscriptionTransferReasonCodes).find(
                      (key) =>
                        SubscriptionTransferReasonCodes[key as keyof typeof SubscriptionTransferReasonCodes] ===
                        reason?.label
                    );
                    formState.customValue = reason?.label;
                    setCurrFormState(formState);
                    //@ts-ignore
                    fieldProps.onChange(reason);
                  }}
                  className="billing-admin"
                ></Select>
              )}
            </Field>
          </div>
        </FormSection>

        <div className="billingAdmins">
          <FormSection>
            <div className="form-component-width">
              <Field
                name="currentBillingAdmin"
                id="currentBillingAdmin"
                label="Choose the consenting billing admins of the current and new billing accounts"
                defaultValue={formState?.currentBillingAdmin}
                isRequired
              >
                {({ fieldProps }) => (
                  <Select
                    {...fieldProps}
                    isSearchable={false}
                    options={currentBillingAdmins}
                    data-testid="previous-billing-admin"
                    placeholder={'Current billing admin'}
                    value={currentBillingAdmins?.find((admin) => admin.value === formState?.currentBillingAdmin)}
                    //@ts-ignore
                    onChange={(admin: ValueType<SelectItem, false>) => {
                      formState.currentBillingAdmin = admin?.label;
                      setCurrFormState(formState);
                      //@ts-ignore
                      fieldProps.onChange(admin);
                    }}
                    className="billing-admin"
                  ></Select>
                )}
              </Field>
            </div>
          </FormSection>

          <FormSection>
            <div className="form-component-width" data-testid="new-billing-admin">
              <Field name="newBillingAdmin" id="newBillingAdmin" defaultValue={formState?.newBillingAdmin}>
                {({ fieldProps }) => (
                  <Select
                    {...fieldProps}
                    isSearchable={false}
                    options={newBillingAdmins}
                    data-testid="new-billing-admin"
                    placeholder={'New billing admin'}
                    value={newBillingAdmins?.find((admin) => admin.value === formState?.newBillingAdmin)}
                    //@ts-ignore
                    onChange={(admin: ValueType<SelectItem, false>) => {
                      formState.newBillingAdmin = admin?.label;
                      setCurrFormState(formState);
                      //@ts-ignore
                      fieldProps.onChange(admin);
                    }}
                    className="billing-admin spacing"
                  ></Select>
                )}
              </Field>
            </div>
          </FormSection>
        </div>

        <FormSection>
          <Checkbox
            isChecked={formState?.termsOfTransfer}
            onChange={() => {
              (formState.termsOfTransfer = !isChecked), setCurrFormState(formState), setIsChecked(!isChecked);
            }}
            isDisabled={hasBothBillingAdmins}
            label="Both billing admins have consented to the transfer"
            name="controlled-checkbox"
            isRequired
          />
        </FormSection>

        <FormSection>
          <Field
            name="supportTicketLink"
            label="Support ticket number"
            defaultValue={formState?.supportTicketLink}
            isRequired
            validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
          >
            {({ fieldProps, error }) => (
              <>
                <Textfield
                  {...fieldProps}
                  name="supportTicketLink"
                  onChange={(e) => {
                    formState.supportTicketLink = e.currentTarget.value;
                    setCurrFormState(formState);
                    fieldProps.onChange(e);
                  }}
                  testId="support-ticket-link"
                  placeholder={'Add GSAC ticket number'}
                />
                {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
              </>
            )}
          </Field>
        </FormSection>

        <FormSection>
          <Field<string, HTMLTextAreaElement>
            label="Internal comment"
            name="notes"
            defaultValue={formState?.notes}
            isRequired
          >
            {({ fieldProps }) => (
              <>
                <TextArea
                  {...fieldProps}
                  name="notes"
                  onChange={(e) => {
                    (formState.notes = e.currentTarget.value), setCurrFormState(formState);
                    fieldProps.onChange(e);
                  }}
                  testId="internal-notes"
                  placeholder={'Add comment'}
                />
              </>
            )}
          </Field>
        </FormSection>
      </div>
    </>
  );
};
